import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';
import {
  startRiddledInGame,
  resumeRiddledInGame,
  submitRiddledInGuess,
  revealRiddledInWord,
} from '../../../utils/riddledInApi';
import {
  RIDDLED_IN_COST,
  calculateMatchPercentage,
  calculateLettersInWordMask,
  isRiddledInGuessComplete,
} from '../../../constants/games/riddledInGame';
import {
  GUEST_DEMO_BUTTON_LABEL,
  RIDDLED_IN_DEMO_NOTE,
} from '../../../constants/games/demoModeCopy';
import { pickRandomRiddledInDemoRound } from '../../../constants/games/riddledInDemoRounds';
import { fetchEnglishDefinition } from '../../../utils/dictionaryDefinition';
import Confetti from 'react-confetti';
import RiddledInGuessCells from './RiddledInGuessCells';
import RiddledInPowerups from './RiddledInPowerups';
import RewardSummary from '../shared/RewardSummary';
import './RiddledInGame.css';
import { riddledInDebug } from '../../../utils/debug/riddledInDebug';
import { useActiveTimer } from '../../../utils/hooks/useActiveTimer';
import { startRound, finishRound } from '../../../utils/gameRoundApi';

const RiddledInGame = () => {
  const { user, coins, refreshCoins, setSeasonPoints, setLifetimePoints } = useProfile();

  // Game state machine: idle → loading → playing → won | revealed
  const [phase, setPhase] = useState('idle');
  const [gameId, setGameId] = useState('');
  const [riddle, setRiddle] = useState('');
  const [wordLength, setWordLength] = useState(0);
  const [revealedWord, setRevealedWord] = useState('');
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [isSubmittingGuess, setIsSubmittingGuess] = useState(false);
  const [error, setError] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [solvedWord, setSolvedWord] = useState('');
  const [definitionState, setDefinitionState] = useState({
    loading: false,
    loaded: false,
    error: '',
    data: null,
  });
  const [hasHintImage, setHasHintImage] = useState(false);
  const [pointsAwarded, setPointsAwarded] = useState(0);
  const [firstGameBonus, setFirstGameBonus] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoSecretWord, setDemoSecretWord] = useState('');

  const inputRef = useRef(null);
  const guessListRef = useRef(null);
  const resumeSeqRef = useRef(0);
  const roundIdRef = useRef(null);
  const lastDemoRoundIdRef = useRef('');
  const activeTimer = useActiveTimer();

  const coinsSafe = typeof coins === 'number' ? coins : 0;
  const canAfford = !user || coinsSafe >= RIDDLED_IN_COST;
  const isGuessComplete = isRiddledInGuessComplete(currentGuess, wordLength);

  // ── Resume last game on mount (so leaving to homepage doesn't wipe the riddle) ──
  useEffect(() => {
    let cancelled = false;
    const seq = (resumeSeqRef.current += 1);
    const userId = user?.id || null;

    // If user signs out, allow future auto-resume when they sign back in.
    if (!userId) {
      riddledInDebug('resume:skip_no_user');
      roundIdRef.current = null;
      setPhase('idle');
      setIsDemoMode(false);
      setDemoSecretWord('');
      setHasHintImage(false);
      return;
    }

    (async () => {
      setError('');
      setPhase('loading');
      riddledInDebug('resume:start', { userId, seq });
      try {
        const data = await resumeRiddledInGame();
        if (cancelled) {
          riddledInDebug('resume:ignored_cancelled', { seq });
          return;
        }
        if (seq !== resumeSeqRef.current) {
          riddledInDebug('resume:ignored_stale', { seq, latestSeq: resumeSeqRef.current });
          return;
        }

        riddledInDebug('resume:success', {
          gameId: data?.gameId || null,
          status: data?.status || null,
          wordLength: data?.wordLength || null,
          guessesCount: Array.isArray(data?.guesses) ? data.guesses.length : 0,
          seq,
        });

        setGameId(data.gameId);
        setRiddle(data.riddle);
        setWordLength(data.wordLength);
        setIsDemoMode(false);
        setDemoSecretWord('');

        const restoredGuesses = Array.isArray(data?.guesses) ? data.guesses : [];
        setGuesses(restoredGuesses);

        // If we already revealed earlier, show revealed state + word.
        if (data.status === 'revealed') {
          roundIdRef.current = null;
          setRevealedWord(data.word || '');
          setPhase('revealed');
          return;
        }

        // If solved earlier and we have a correct guess stored, show won state.
        if (data.status === 'solved') {
          roundIdRef.current = null;
          const correct = restoredGuesses.find((g) => g?.isCorrect);
          if (correct?.word) setSolvedWord(correct.word);
          setPhase('won');
          return;
        }

        // Restored rounds need fresh tracking because timer/ref state resets on reload.
        activeTimer.start();
        startRound({ gameType: 'riddled_in' }).then((id) => {
          roundIdRef.current = id;
        });
        setPhase('playing');
        setTimeout(() => inputRef.current?.focus(), 100);
      } catch (err) {
        // No resume? go back to idle cleanly.
        if (cancelled) {
          riddledInDebug('resume:failed_ignored_cancelled', { seq });
          return;
        }
        if (seq !== resumeSeqRef.current) {
          riddledInDebug('resume:failed_ignored_stale', { seq, latestSeq: resumeSeqRef.current });
          return;
        }
        riddledInDebug('resume:failed', {
          message: err?.message || String(err),
          code: err?.code || null,
          status: err?.status || null,
          seq,
        });
        setPhase('idle');
      }
    })();

    return () => {
      cancelled = true;
      riddledInDebug('resume:cleanup', { seq });
    };
  }, [user?.id]);

  // ── Start a new game ──────────────────────────────────────
  const handleStartGame = useCallback(async () => {
    const shouldStartDemo = !user;
    if (!shouldStartDemo && !canAfford) {
      setError('Not enough coins.');
      return;
    }

    setPhase('loading');
    setError('');
    setGameId('');
    setGuesses([]);
    setCurrentGuess('');
    setIsSubmittingGuess(false);
    setShowConfetti(false);
    setRevealedWord('');
    setSolvedWord('');
    setDefinitionState({ loading: false, loaded: false, error: '', data: null });
    setPointsAwarded(0);
    setFirstGameBonus(false);
    setIsDemoMode(shouldStartDemo);
    setDemoSecretWord('');
    setHasHintImage(false);
    roundIdRef.current = null;

    try {
      if (shouldStartDemo) {
        const demoRound = pickRandomRiddledInDemoRound(lastDemoRoundIdRef.current);
        lastDemoRoundIdRef.current = demoRound.id;
        setDemoSecretWord(demoRound.word);
        setRiddle(demoRound.riddle);
        setGameId(`demo-local:${demoRound.id}`);
        setWordLength(demoRound.word.length);
        setPhase('playing');
        setTimeout(() => inputRef.current?.focus(), 100);
        return;
      }

      riddledInDebug('start:start', { userId: user?.id || null, coinsSafe });
      const data = await startRiddledInGame();
      riddledInDebug('start:success', {
        gameId: data?.gameId || null,
        wordLength: data?.wordLength || null,
        isDemo: shouldStartDemo,
      });
      setRiddle(data.riddle);
      setGameId(data.gameId);
      setWordLength(data.wordLength);
      try {
        localStorage.setItem('riddled_in_last_game_id', data.gameId);
      } catch {
        // ignore
      }
      setPhase('playing');
      setTimeout(() => inputRef.current?.focus(), 100);

      if (!shouldStartDemo) {
        await refreshCoins?.();

        // Leaderboard: start round tracking (fire-and-forget)
        activeTimer.start();
        startRound({ gameType: 'riddled_in' }).then((id) => {
          roundIdRef.current = id;
        });
      }
    } catch (err) {
      riddledInDebug('start:failed', {
        message: err?.message || String(err),
        code: err?.code || null,
        status: err?.status || null,
      });
      setError(err.message || 'Failed to start game.');
      setPhase('idle');
      setIsDemoMode(false);
      setDemoSecretWord('');
      if (!shouldStartDemo) {
        await refreshCoins?.();
      }
    }
  }, [user, canAfford, refreshCoins, coinsSafe]);

  // ── Submit a guess ────────────────────────────────────────
  const handleSubmitGuess = useCallback(async () => {
    const trimmed = currentGuess.trim();
    if (!isGuessComplete || !trimmed || phase !== 'playing') return;
    if (!gameId) return;
    if (isSubmittingGuess) return;

    setError('');

    let res;
    try {
      setIsSubmittingGuess(true);
      if (isDemoMode) {
        if (!demoSecretWord) {
          throw new Error('Demo round is not ready. Start a new demo round.');
        }
        const percentage = calculateMatchPercentage(demoSecretWord, trimmed);
        res = {
          percentage,
          isCorrect: trimmed.toLowerCase() === demoSecretWord.toLowerCase(),
          lettersInWord: calculateLettersInWordMask(demoSecretWord, trimmed),
          status: 'active',
          wordLength: demoSecretWord.length,
        };
      } else {
        res = await submitRiddledInGuess({ gameId, guess: trimmed });
      }
    } catch (err) {
      setError(err.message || 'Guess failed.');
      return;
    } finally {
      setIsSubmittingGuess(false);
    }

    const percentage = res.percentage;
    const isCorrect = res.isCorrect;
    const lettersInWord = Array.isArray(res?.lettersInWord)
      ? res.lettersInWord.map((value) => Boolean(value))
      : [];
    const nextGuess = {
      word: trimmed,
      percentage,
      isCorrect,
      ...(lettersInWord.length > 0 ? { lettersInWord } : {}),
    };

    // Newest guess goes on top
    setGuesses((prev) => {
      const next = [nextGuess, ...prev];
      return next;
    });
    setCurrentGuess('');

    if (isCorrect) {
      setSolvedWord(trimmed);
      setPhase('won');
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);

      // Leaderboard: stop timer and record win, capture points
      const activeMs = !isDemoMode ? activeTimer.stop() : 0;
      if (!isDemoMode && roundIdRef.current) {
        const allGuesses = [nextGuess, ...guesses];
        finishRound({
          roundId: roundIdRef.current,
          won: true,
          guessesUsed: allGuesses.length,
          activeTimeMs: activeMs,
          metadata: {
            firstGuessPct: allGuesses.length > 0 ? allGuesses[allGuesses.length - 1]?.percentage : null,
            maxPct: 100,
          },
        }).then((result) => {
          if (result?.pointsAwarded) {
            setPointsAwarded(result.pointsAwarded);
            setFirstGameBonus(result.firstGameBonus);
            // Keep the homepage badge in sync
            setSeasonPoints((prev) => (prev ?? 0) + result.pointsAwarded);
            setLifetimePoints((prev) => (prev ?? 0) + result.pointsAwarded);
          }
        });
        roundIdRef.current = null;
      }
    }

    // With newest-on-top, scroll list to top after inserting
    setTimeout(() => {
      guessListRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }, 50);

    // Re-focus input
    inputRef.current?.focus();
  }, [
    currentGuess,
    demoSecretWord,
    gameId,
    isDemoMode,
    isGuessComplete,
    isSubmittingGuess,
    phase,
  ]);

  // Keep desktop keyboard input working even if focus drifts away from the hidden cell input.
  useEffect(() => {
    if (phase !== 'playing') return;

    const handleWindowKeyDown = (e) => {
      if (isSubmittingGuess) return;
      if (e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const target = e.target;
      const targetElement = target instanceof Element ? target : null;
      const tagName = targetElement?.tagName?.toLowerCase() || '';
      const isEditableTarget =
        Boolean(targetElement?.isContentEditable) ||
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select';
      const isRiddledInCellsTarget = Boolean(targetElement?.closest('.riddled-in-cells'));

      // If the hidden cells input already has focus, let it handle key events.
      if (isRiddledInCellsTarget) return;
      // Don't hijack typing in other editable controls.
      if (isEditableTarget) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        if (isGuessComplete) {
          handleSubmitGuess();
        } else {
          inputRef.current?.focus();
        }
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        setCurrentGuess((prev) => prev.slice(0, -1));
        inputRef.current?.focus();
        return;
      }

      if (/^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault();
        setCurrentGuess((prev) => {
          if (prev.length >= wordLength) return prev;
          return `${prev}${e.key.toUpperCase()}`;
        });
        inputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleWindowKeyDown);
    return () => window.removeEventListener('keydown', handleWindowKeyDown);
  }, [phase, isSubmittingGuess, isGuessComplete, wordLength, handleSubmitGuess]);

  // ── Give up ───────────────────────────────────────────────
  const handleGiveUp = useCallback(async () => {
    if (!gameId) return;
    setError('');
    try {
      if (isDemoMode && !demoSecretWord) {
        throw new Error('Demo round is not ready. Start a new demo round.');
      }
      const res = isDemoMode
        ? { word: demoSecretWord.toLowerCase() }
        : await revealRiddledInWord({ gameId });
      setRevealedWord(res.word || '');
      setPhase('revealed');

      // Leaderboard: stop timer and record loss, capture points
      const activeMs = !isDemoMode ? activeTimer.stop() : 0;
      if (!isDemoMode && roundIdRef.current) {
        const maxPct = guesses.reduce((max, g) => Math.max(max, g.percentage || 0), 0);
        finishRound({
          roundId: roundIdRef.current,
          won: false,
          guessesUsed: guesses.length,
          activeTimeMs: activeMs,
          metadata: {
            firstGuessPct: guesses.length > 0 ? guesses[guesses.length - 1]?.percentage : null,
            maxPct,
          },
        }).then((result) => {
          if (result?.pointsAwarded) {
            setPointsAwarded(result.pointsAwarded);
            setFirstGameBonus(result.firstGameBonus);
            // Keep the homepage badge in sync
            setSeasonPoints((prev) => (prev ?? 0) + result.pointsAwarded);
            setLifetimePoints((prev) => (prev ?? 0) + result.pointsAwarded);
          }
        });
        roundIdRef.current = null;
      }

      // Keep revealed word in guesses store too (optional)
      // and clear definition UI.
      setDefinitionState({ loading: false, loaded: false, error: '', data: null });
    } catch (err) {
      setError(err.message || 'Reveal failed.');
    }
  }, [activeTimer, demoSecretWord, gameId, guesses, isDemoMode]);

  const handleLoadDefinition = useCallback(async () => {
    const w = String(solvedWord || '').trim();
    if (!w) return;

    // toggle off if already loaded
    if (definitionState.loaded) {
      setDefinitionState({ loading: false, loaded: false, error: '', data: null });
      return;
    }

    setDefinitionState({ loading: true, loaded: false, error: '', data: null });
    try {
      const data = await fetchEnglishDefinition(w);
      setDefinitionState({ loading: false, loaded: true, error: '', data });
    } catch (err) {
      setDefinitionState({
        loading: false,
        loaded: false,
        error: err.message || 'Failed to load definition.',
        data: null,
      });
    }
  }, [solvedWord, definitionState.loaded]);

  // ── Percentage → color (red → green gradient) ─────────────
  const getPercentageColor = (pct) => {
    const hue = Math.round((pct / 100) * 120); // 0 = red, 120 = green
    return `hsl(${hue}, 75%, 45%)`;
  };

  const renderGuessWord = (guessEntry) => {
    const rawWord = String(guessEntry?.word || '');
    const word = rawWord.toUpperCase();
    const knownSecretWord =
      phase === 'revealed'
        ? String(revealedWord || '')
        : phase === 'won'
          ? String(solvedWord || guesses.find((g) => g?.isCorrect)?.word || '')
          : '';

    const hasServerMask =
      Array.isArray(guessEntry?.lettersInWord) &&
      guessEntry.lettersInWord.length === word.length;

    const lettersInWord = hasServerMask
      ? guessEntry.lettersInWord
      : knownSecretWord
        ? calculateLettersInWordMask(knownSecretWord, rawWord)
        : [];

    return [...word].map((letter, index) => (
      <span
        key={`${word}-${index}`}
        className={`riddled-in-guess-letter ${lettersInWord[index] ? 'present' : ''}`.trim()}
      >
        {letter}
      </span>
    ));
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="riddled-in">
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={300}
          gravity={0.2}
          colors={[
            '#FFD700',
            '#FFA500',
            '#FF6347',
            '#FF69B4',
            '#4169E1',
            '#9B59B6',
          ]}
        />
      )}

      {error && <div className="riddled-in-error">{error}</div>}

      {/* ── Idle: intro + start button ─────────────────────── */}
      {phase === 'idle' && (
        <div className="riddled-in-intro">
          <div className="riddled-in-icon">🧩</div>
          <h2 className="riddled-in-title">Riddled-In</h2>
          <p className="riddled-in-desc">
            A riddle will be generated for a secret word. Type your guesses and
            watch the percentage climb as your letters match. Hit 100% to solve
            it!
          </p>
          {!user && (
            <p className="riddled-in-demo-note">{RIDDLED_IN_DEMO_NOTE}</p>
          )}
          <button
            className="riddled-in-start-btn"
            onClick={handleStartGame}
            disabled={Boolean(user) && !canAfford}
          >
            {!user
              ? GUEST_DEMO_BUTTON_LABEL
              : !canAfford
                ? 'Not Enough Coins'
                : `Start Game \u2022 ${RIDDLED_IN_COST} coins`}
          </button>
        </div>
      )}

      {/* ── Loading ────────────────────────────────────────── */}
      {phase === 'loading' && (
        <div className="riddled-in-loading">
          <div className="riddled-in-spinner" />
          <p>{isDemoMode ? 'Loading demo riddle...' : 'Generating your riddle...'}</p>
        </div>
      )}

      {/* ── Playing / Won / Revealed ───────────────────────── */}
      {(phase === 'playing' || phase === 'won' || phase === 'revealed') && (
        <div className="riddled-in-game">
          <div className={`riddled-in-top-row ${hasHintImage ? 'has-image' : ''}`.trim()}>
            {/* Riddle Card */}
            <div className="riddled-in-riddle-card">
              <div className="riddled-in-riddle-label">🧩 Riddle</div>
              <p className="riddled-in-riddle-text">{riddle}</p>
              <div className="riddled-in-hint">
                The word has <strong>{wordLength}</strong> letter
                {wordLength !== 1 ? 's' : ''}
              </div>
              {isDemoMode && (
                <div className="riddled-in-demo-inline-note">{RIDDLED_IN_DEMO_NOTE}</div>
              )}
            </div>

            <RiddledInPowerups
              user={user}
              coins={coinsSafe}
              gameId={gameId}
              phase={phase}
              refreshCoins={refreshCoins}
              onHasImageChange={setHasHintImage}
              showDemoLockedState={isDemoMode}
            />
          </div>

          {/* Input (only while playing) */}
          {phase === 'playing' && (
            <>
              <div className="riddled-in-input-row">
                <RiddledInGuessCells
                  ref={inputRef}
                  value={currentGuess}
                  onChange={(next) => setCurrentGuess(next)}
                  onSubmit={() => {
                    if (isSubmittingGuess || !isGuessComplete) return;
                    handleSubmitGuess();
                  }}
                  maxLength={wordLength}
                  disabled={isSubmittingGuess}
                  keepFocused
                />
                <button
                  className="riddled-in-guess-btn"
                  onClick={handleSubmitGuess}
                  disabled={!isGuessComplete || isSubmittingGuess}
                >
                  {isSubmittingGuess ? 'Loading...' : 'Guess'}
                </button>
              </div>

              {guesses.length >= 3 && (
                <button
                  className="riddled-in-give-up-btn"
                  onClick={handleGiveUp}
                >
                  Give Up
                </button>
              )}
            </>
          )}

          {/* Won banner */}
          {phase === 'won' && (
            <div className="riddled-in-won">
              <div className="riddled-in-won-emoji">🎉</div>
              <div className="riddled-in-won-text">
                You got it! Your winning guess was{' '}
                <strong>
                  {(guesses[0]?.word || solvedWord || '').toUpperCase()}
                </strong>
              </div>
              <div className="riddled-in-won-stats">
                Solved in {guesses.length}{' '}
                {guesses.length === 1 ? 'guess' : 'guesses'}
              </div>
              {!isDemoMode && (
                <RewardSummary
                  pointsAwarded={pointsAwarded}
                  firstGameBonus={firstGameBonus}
                />
              )}

              <button
                className="riddled-in-secondary-btn"
                onClick={handleLoadDefinition}
                disabled={definitionState.loading || !solvedWord}
              >
                {definitionState.loading
                  ? 'Loading definition...'
                  : definitionState.loaded
                    ? 'Hide definition'
                    : 'Show definition'}
              </button>

              {definitionState.error && (
                <div className="riddled-in-error">{definitionState.error}</div>
              )}

              {definitionState.loaded && definitionState.data && (
                <div className="riddled-in-definition-card">
                  <div className="riddled-in-definition-header">
                    <div className="riddled-in-definition-word">
                      {definitionState.data.word.toUpperCase()}
                    </div>
                    <div className="riddled-in-definition-meta">
                      {definitionState.data.partOfSpeech
                        ? definitionState.data.partOfSpeech
                        : ''}
                      {definitionState.data.phonetic
                        ? ` • ${definitionState.data.phonetic}`
                        : ''}
                    </div>
                  </div>
                  <p className="riddled-in-definition-text">
                    {definitionState.data.definition}
                  </p>
                  {definitionState.data.example && (
                    <div className="riddled-in-definition-example">
                      Example: “{definitionState.data.example}”
                    </div>
                  )}
                </div>
              )}

              <button
                className="riddled-in-start-btn"
                onClick={handleStartGame}
                disabled={Boolean(user) && !canAfford}
              >
                {!user
                  ? 'Play Another Demo Riddle'
                  : !canAfford
                  ? 'Not Enough Coins'
                  : `Play Again \u2022 ${RIDDLED_IN_COST} coins`}
              </button>
            </div>
          )}

          {/* Revealed (give up) banner */}
          {phase === 'revealed' && (
            <div className="riddled-in-reveal">
              <div className="riddled-in-reveal-text">
                The word was{' '}
                <strong>{(revealedWord || '').toUpperCase()}</strong>
              </div>
              {pointsAwarded > 0 && (
                <div style={{ color: '#ffd700', marginTop: '4px', fontSize: '0.95rem' }}>
                  +{pointsAwarded} pts
                </div>
              )}
              <button
                className="riddled-in-start-btn"
                onClick={handleStartGame}
                disabled={Boolean(user) && !canAfford}
              >
                {!user
                  ? 'Try Another Demo Riddle'
                  : !canAfford
                  ? 'Not Enough Coins'
                  : `Try Again \u2022 ${RIDDLED_IN_COST} coins`}
              </button>
            </div>
          )}

          {/* Past guesses */}
          {guesses.length > 0 && (
            <div className="riddled-in-guesses" ref={guessListRef}>
              <div className="riddled-in-guesses-label">
                Guesses ({guesses.length})
              </div>
              {guesses.map((g, i) => (
                <div
                  key={`${g.word}-${g.percentage}-${i}`}
                  className={`riddled-in-guess-row ${g.isCorrect ? 'correct' : ''}`}
                >
                  <span className="riddled-in-guess-word">
                    {renderGuessWord(g)}
                  </span>
                  <div className="riddled-in-guess-bar-track">
                    <div
                      className="riddled-in-guess-bar"
                      style={{
                        width: `${g.percentage}%`,
                        backgroundColor: getPercentageColor(g.percentage),
                      }}
                    />
                  </div>
                  <span
                    className="riddled-in-guess-pct"
                    style={{ color: getPercentageColor(g.percentage) }}
                  >
                    {g.percentage}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RiddledInGame;
